import React, { useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { Heading, LearningContentCard, LearningContentCardProps } from '@atlassian/learning-components';
import { media } from '@atlaskit/primitives/responsive';
import Slider from 'react-slick';
import { times } from 'lodash';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { AdobeLearningPath, cleanAdobeId, fetchAdobeLearningPaths } from 'src/services/http/adobe';

export type ContentItem = Pick<LearningContentCardProps, 'title' | 'description' | 'link' | 'tags'>;

export interface ContentSectionProps {
  title: string;
  catalogId: string;
  adobeAccountId: string | null;
}

export const ContentSection: React.FC<ContentSectionProps> = ({ title, catalogId, adobeAccountId }) => {
  const [adobeLearningPaths, setAdobeLearningPaths] = useState<AdobeLearningPath[]>([]);

  useEffect(() => {
    fetchAdobeLearningPaths([catalogId]).then(setAdobeLearningPaths);
  }, [catalogId]);

  const sliderSettings = {
    arrows: true,
    slidesToShow: 3,
    draggable: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <section css={sectionStyles}>
      <Heading level={3}>{title}</Heading>
      <div css={carouselContainerStyles}>
        <Slider {...sliderSettings}>
          {!adobeLearningPaths.length &&
            times(3, (i) => (
              <div css={slideStyles} key={i}>
                <LearningContentCard isLoading={true} title="" link="" onClick={() => {}} tags={[]} />
              </div>
            ))}
          {adobeLearningPaths.map((lp) => {
            const link = `https://learningmanager.adobe.com/app/learner?accountId=${adobeAccountId}#/learningProgram/${cleanAdobeId(
              lp.id,
            )}`;

            return (
              <div key={lp.id} css={slideStyles}>
                <LearningContentCard
                  title={lp.attributes.localizedMetadata[0].name}
                  description={lp.attributes.localizedMetadata[0].description}
                  link={link}
                  onClick={() => window.open(link, '_blank')}
                  tags={lp.attributes.tags?.map((tag) => ({ label: tag })) || []}
                />
              </div>
            );
          })}
        </Slider>
      </div>
    </section>
  );
};

const slideStyles = css({
  padding: '15px 12px',
});

const carouselContainerStyles = css({
  marginTop: '20px',
  maxWidth: '100%',

  '& .slick-prev, & .slick-next': {
    zIndex: 1,

    '&:before': {
      fontSize: '20px',
      color: '#000',
    },
  },

  '& .slick-prev': {
    left: '-25px',

    [media.below.lg]: {
      left: 0,
    },
  },

  '& .slick-next': {
    right: '-25px',

    [media.below.lg]: {
      right: 0,
    },
  },
});

const sectionStyles = css({
  marginTop: '50px',
});
